import type { LanguageCode } from "../localization/supportedLanguages";

type QuantCopy = {
  seo: { title: string; description: string };
  skip: string;
  nav: {
    label: string;
    approach: string;
    work: string;
    start: string;
    language: string;
    book: string;
    menu: string;
  };
  hero: {
    label: string;
    title: string;
    accent: string;
    description: string;
    cta: string;
    secondary: string;
    note: string;
    visualLabel: string;
    visualCaption: string;
  };
  explorer: {
    label: string;
    introduction: string;
    title: string;
    question: string;
    evidence: string;
    analysis: string;
    action: string;
    note: string;
    scenarios: Array<{
      label: string;
      question: string;
      evidence: string;
      analysis: string;
      action: string;
    }>;
  };
  context: { label: string; text: string };
  approach: {
    label: string;
    title: string;
    accent: string;
    description: string;
    detail: string;
    link: string;
    steps: Array<{ title: string; description: string; output: string }>;
  };
  work: {
    label: string;
    title: string;
    summary: string;
    impact: string | null;
    cases: Array<{ name: string; description: string; href: string }>;
  };
  start: {
    label: string;
    title: string;
    accent: string;
    description: string;
    cta: string;
    note: string;
    steps: Array<{ title: string; description: string }>;
  };
  faq: { title: string; items: Array<{ question: string; answer: string }> };
  footer: {
    label: string;
    tagline: string;
    privacy: string;
    terms: string;
    support: string;
  };
};

export const quantCopy: Record<LanguageCode, QuantCopy> = {
  en: {
    seo: {
      title: "AI Agents for Business Growth | YouWo",
      description:
        "YouWo combines connected data, memory, and hands-on AI implementation around your business goals. Define the baseline, run the workflow, and measure the return.",
    },
    skip: "Skip to content",
    nav: {
      label: "Main navigation",
      approach: "Our approach",
      work: "Engagements",
      start: "Work with us",
      language: "Language",
      book: "Discuss your growth",
      menu: "Menu",
    },
    hero: {
      label: "YOUR AI QUANT FOR BUSINESS",
      title: "Turn ambition",
      accent: "into growth.",
      description:
        "We help ambitious businesses find their advantage and act on it. Quantitative analysis. Focused growth experiments. AI that does the work.",
      cta: "Build your next stage",
      secondary: "See our engagements",
      note: "Built for leaders. Focused on revenue.",
      visualLabel: "INTELLIGENCE INTO ACTION",
      visualCaption: "Think rigorously. Move decisively.",
    },
    explorer: {
      label: "FROM QUESTION TO ACTION",
      introduction:
        "Start with a commercial objective. Follow the evidence to a decision, then put the next move to the test.",
      title: "What would you like to improve?",
      question: "START WITH A BETTER QUESTION",
      evidence: "Bring the evidence",
      analysis: "Find the opportunity",
      action: "A next move to test",
      note: "Illustrative workflow · Select a goal to explore.",
      scenarios: [
        {
          label: "Revenue",
          question: "Where should our next customer come from?",
          evidence: "Sales history, customer conversations, and channel costs.",
          analysis:
            "Compare customer segments and channels. Make the assumptions explicit.",
          action:
            "Run a focused channel experiment. Track conversion and cost per customer.",
        },
        {
          label: "Margins",
          question: "Which sales are actually worth growing?",
          evidence: "Product sales, fulfillment costs, and discounts.",
          analysis:
            "Compare contribution margins across products and channels.",
          action:
            "Test a product or channel priority. Measure its effect on profit, not just sales.",
        },
        {
          label: "Operations",
          question: "What is slowing the business down?",
          evidence: "Order history, process steps, and team handoffs.",
          analysis: "Identify repeated delays and the assumptions behind them.",
          action:
            "Improve one recurring workflow. Track turnaround time against the baseline.",
        },
      ],
    },
    context: {
      label: "THE YOUWO ADVANTAGE",
      text: "Analytical rigor. Commercial instinct. Relentless execution.",
    },
    approach: {
      label: "OUR APPROACH",
      title: "Find the edge.",
      accent: "Then put it to work.",
      description:
        "An AI Quant brings data, experiments, and disciplined decision-making to the everyday work of growing a business.",
      detail:
        "We work alongside you to understand the numbers, quantify risks, test assumptions, and execute sales and distribution work. Each cycle makes the next decision better informed.",
      link: "Start with one business question",
      steps: [
        {
          title: "Understand.",
          description:
            "Connect the business question to your data, customers, and market. Establish what is known and what needs testing.",
          output: "A clear baseline",
        },
        {
          title: "Prioritize.",
          description:
            "Compare opportunities by potential value, cost, and uncertainty. Choose a focused experiment worth running.",
          output: "A testable growth hypothesis",
        },
        {
          title: "Execute.",
          description:
            "Put AI to work on the agreed plan: research, analysis, sales preparation, follow-ups, and repeatable operations.",
          output: "Work you can review",
        },
        {
          title: "Learn.",
          description:
            "Track what happened against the starting point. Use the evidence to decide what to scale, change, or stop.",
          output: "An evidence-led next move",
        },
      ],
    },
    work: {
      "label": "SELECTED CASE STUDIES",
      "summary": "Selected work across business intelligence, international operations, marketing, and manufacturing.",
      "impact": null,
      "cases": [
        {
          "name": "URide",
          "description": "Connected business data. Analysis that carries context forward.",
          "href": "/blog/uride-business-context"
        },
        {
          "name": "YOLO",
          "description": "International operations. An agent that learns with every round.",
          "href": "/blog/international-business-learning-loop"
        },
        {
          "name": "Ferns Kids",
          "description": "Agent orchestration for marketing and brand awareness.",
          "href": "/blog/ferns-kids-marketing-orchestration"
        },
        {
          "name": "Manufacturing",
          "description": "Custom algorithms built around a specific operating challenge.",
          "href": "/blog/manufacturing-custom-algorithms"
        }
      ],
      "title": "Built around the business."
    },
    start: {
      label: "WORK WITH US",
      title: "Build what’s next.",
      accent: "With an edge.",
      description:
        "A new market. A sales bottleneck. A spreadsheet with more questions than answers. Start with the opportunity in front of you.",
      cta: "Let’s talk about your growth",
      note: "We agree on scope, pricing, and success measures together.",
      steps: [
        {
          title: "Tell us what you’re trying to grow.",
          description:
            "Share the business, the decision, and what a useful outcome would look like.",
        },
        {
          title: "Scope one focused engagement.",
          description:
            "Define the data, the work, and the first deliverable. Agree on how to measure progress.",
        },
        {
          title: "Build, test, and review together.",
          description:
            "Work through the analysis and execution, then review the evidence before the next move.",
        },
      ],
    },
    faq: {
      title: "A few things to know.",
      items: [
        {
          question: "What do you mean by “AI Quant”?",
          answer:
            "Quant is short for quantitative: using data and models to make decisions. YouWo applies that discipline to business growth, combining AI analysis with practical execution across sales, distribution, and operations.",
        },
        {
          question: "Is this software or a hands-on engagement?",
          answer:
            "Start with a hands-on engagement around a business question. We scope the analysis and execution with you and use AI to do the work. You can also explore the product through Get Started.",
        },
        {
          question: "Do I need perfectly organized data?",
          answer:
            "Bring what you have. The first conversation establishes which sources are useful, what is missing, and whether there is enough evidence to start a meaningful analysis or experiment.",
        },
        {
          question: "Are the featured projects completed case studies?",
          answer:
            "They are current engagements. The descriptions show the focus of the work and the intended goals. Completed deliverables and measured outcomes will be added when available.",
        },
      ],
    },
    footer: {
      label: "YouWo links",
      tagline: "Intelligence for the next stage.",
      privacy: "Privacy",
      terms: "Terms",
      support: "Support",
    },
  },
  zh: {
    seo: {
      title: "YouWo — 用 AI 量化思维推动业务增长",
      description:
        "YouWo 围绕业务目标，连接数据、记忆与 AI 落地。明确基线，执行流程，衡量实际回报。",
    },
    skip: "跳转到正文",
    nav: {
      label: "主导航",
      approach: "我们的方法",
      work: "当前项目",
      start: "与我们合作",
      language: "语言",
      book: "聊一聊",
      menu: "菜单",
    },
    hero: {
      label: "你的企业增长 AI QUANT",
      title: "把雄心",
      accent: "变成增长。",
      description:
        "帮助有雄心的企业找到优势，付诸行动。量化分析、聚焦的增长实验，以及真正推进工作的 AI。",
      cta: "迈向下一个阶段",
      secondary: "了解合作项目",
      note: "为企业领导者而建，聚焦收入增长。",
      visualLabel: "从智能到行动",
      visualCaption: "严谨思考，果断行动。",
    },
    explorer: {
      label: "从问题到行动",
      introduction: "从一个商业目标开始，以证据支持决策，再验证下一步行动。",
      title: "你想改善业务的哪个方面？",
      question: "从一个更好的问题开始",
      evidence: "收集依据",
      analysis: "发现机会",
      action: "值得验证的下一步",
      note: "工作流程示例 · 选择目标查看。",
      scenarios: [
        {
          label: "收入",
          question: "下一位客户应该从哪里来？",
          evidence: "销售记录、客户沟通和渠道成本。",
          analysis: "比较客户群体与渠道，明确每个判断的假设。",
          action: "开展一次聚焦的渠道实验，跟踪转化率与获客成本。",
        },
        {
          label: "利润",
          question: "哪些销售真正值得扩大？",
          evidence: "产品销量、履约成本和折扣。",
          analysis: "比较不同产品和渠道的贡献利润。",
          action: "测试产品或渠道的优先级，衡量对利润及销售额的影响。",
        },
        {
          label: "运营",
          question: "是什么拖慢了业务？",
          evidence: "订单记录、流程环节和团队交接。",
          analysis: "找到反复出现的延误，验证背后的原因。",
          action: "改善一项重复流程，将周转时间与基线进行比较。",
        },
      ],
    },
    context: {
      label: "YOUWO 的优势",
      text: "严谨的分析。敏锐的商业判断。持续的执行力。",
    },
    approach: {
      label: "我们的方法",
      title: "找到优势。",
      accent: "付诸行动。",
      description:
        "AI Quant 将数据、实验与严谨的决策方法，带入推动企业增长的日常工作。",
      detail:
        "我们与你一起理解数据、量化风险、验证假设，并执行销售与分销工作，让每一轮行动都为下一次决策提供依据。",
      link: "从一个业务问题开始",
      steps: [
        {
          title: "理解。",
          description:
            "将业务问题与你的数据、客户和市场联系起来，明确已知事实与需要验证的假设。",
          output: "清晰的起点",
        },
        {
          title: "排序。",
          description:
            "比较机会的潜在价值、成本和不确定性，选择一项值得开展的实验。",
          output: "可验证的增长假设",
        },
        {
          title: "执行。",
          description:
            "用 AI 推进双方约定的计划：调研、分析、销售准备、跟进与重复运营工作。",
          output: "可审阅的工作成果",
        },
        {
          title: "学习。",
          description:
            "将实际情况与起点对比，以证据决定哪些行动应当扩大、调整或停止。",
          output: "有依据的下一步",
        },
      ],
    },
    work: {
      "label": "精选案例",
      "summary": "从商业分析、国际业务，到品牌营销与制造业的精选实践。",
      "impact": null,
      "cases": [
        {
          "name": "URide",
          "description": "连接业务数据，让分析承接已有背景。",
          "href": "/blog/uride-business-context"
        },
        {
          "name": "YOLO",
          "description": "贯通国际业务，让智能体在每轮工作中学习。",
          "href": "/blog/international-business-learning-loop"
        },
        {
          "name": "Ferns Kids",
          "description": "通过智能体编排，支持营销与品牌认知。",
          "href": "/blog/ferns-kids-marketing-orchestration"
        },
        {
          "name": "制造业",
          "description": "针对具体运营挑战，开发并优化定制算法。",
          "href": "/blog/manufacturing-custom-algorithms"
        }
      ],
      "title": "围绕业务，量身构建。"
    },
    start: {
      label: "与我们合作",
      title: "创造下一阶段。",
      accent: "带着优势出发。",
      description:
        "一个新市场、一个销售瓶颈，或一份让人无从下手的表格。从眼前的机会开始。",
      cta: "聊聊你的增长目标",
      note: "我们会共同确定范围、价格和成功指标。",
      steps: [
        {
          title: "告诉我们你希望增长什么。",
          description: "介绍业务、需要做出的决策，以及什么样的结果对你有用。",
        },
        {
          title: "确定一次聚焦的合作。",
          description: "明确数据、工作内容和首项交付，并约定如何衡量进展。",
        },
        {
          title: "共同构建、验证和复盘。",
          description: "推进分析与执行，审阅证据，再决定下一步。",
        },
      ],
    },
    faq: {
      title: "你可能想了解。",
      items: [
        {
          question: "“AI Quant” 是什么意思？",
          answer:
            "Quant 代表量化，即用数据和模型做决策。YouWo 将这种方法用于业务增长，把 AI 分析与销售、分销和运营中的实际执行结合起来。",
        },
        {
          question: "这是软件产品，还是提供实际服务？",
          answer:
            "我们从一个具体业务问题出发，开展实际合作。与你确定分析和执行范围，并用 AI 推进工作。也可以点击“开始使用”探索产品。",
        },
        {
          question: "数据需要提前整理得很完善吗？",
          answer:
            "先带上现有资料即可。首次交流会确定哪些来源有用、还缺什么，以及是否有足够依据开始有意义的分析或实验。",
        },
        {
          question: "展示的项目已经完成了吗？",
          answer:
            "这些是正在进行的合作，描述的是当前工作重点与预期目标。完成交付、获得可衡量的结果后，我们会补充更新。",
        },
      ],
    },
    footer: {
      label: "YouWo 链接",
      tagline: "以智能推动下一阶段。",
      privacy: "隐私政策",
      terms: "服务条款",
      support: "支持",
    },
  },
  ja: {
    seo: {
      title: "YouWo — 事業成長のための AI Quant",
      description:
        "YouWo は事業の目標を起点に、データ、記憶、AI の実装をつなぎます。基準を定め、業務を動かし、得られた価値を測ります。",
    },
    skip: "本文へ移動",
    nav: {
      label: "メインナビゲーション",
      approach: "アプローチ",
      work: "進行中の取り組み",
      start: "ご相談",
      language: "言語",
      book: "相談する",
      menu: "メニュー",
    },
    hero: {
      label: "あなたの事業成長を支える AI QUANT",
      title: "大きな志を、",
      accent: "確かな成長へ。",
      description:
        "成長を目指す企業が強みを見つけ、行動に移すことを支援します。定量分析、的を絞った成長実験、そして仕事を進める AI。",
      cta: "次のステージへ",
      secondary: "取り組みを見る",
      note: "経営を担うリーダーのために。売上の成長に集中。",
      visualLabel: "知性を行動へ",
      visualCaption: "緻密に考え、果敢に動く。",
    },
    explorer: {
      label: "問いから行動へ",
      introduction:
        "事業の目標から始め、根拠に沿って判断し、次の一手を検証します。",
      title: "事業のどこを改善したいですか？",
      question: "よりよい問いから始める",
      evidence: "根拠を集める",
      analysis: "機会を見つける",
      action: "検証する次の一手",
      note: "ワークフローの例 · 目標を選んでご覧ください。",
      scenarios: [
        {
          label: "売上",
          question: "次の顧客をどこで獲得するべきか？",
          evidence: "販売履歴、顧客との会話、チャネル費用。",
          analysis: "顧客層とチャネルを比較し、判断の前提を明らかにします。",
          action: "一つのチャネルで実験し、転換率と顧客獲得費用を追跡します。",
        },
        {
          label: "利益",
          question: "本当に伸ばす価値がある売上は？",
          evidence: "商品別売上、配送などの履行費用、割引。",
          analysis: "商品やチャネルごとの限界利益を比較します。",
          action:
            "商品やチャネルの優先順位を試し、利益と売上への影響を測ります。",
        },
        {
          label: "業務",
          question: "事業のスピードを落としているものは？",
          evidence: "受注履歴、業務手順、チーム間の引き継ぎ。",
          analysis: "繰り返す遅延を見つけ、その原因を検証します。",
          action: "定型業務を一つ改善し、所要時間を改善前と比較します。",
        },
      ],
    },
    context: {
      label: "YOUWO の強み",
      text: "緻密な分析。事業への洞察。継続的な実行力。",
    },
    approach: {
      label: "アプローチ",
      title: "強みを見つけ、",
      accent: "行動に変える。",
      description:
        "AI Quant は、データ、実験、体系的な意思決定を、事業を成長させる日々の仕事に取り入れます。",
      detail:
        "一緒に数字を理解し、リスクを定量化し、仮説を検証して、販売・流通の業務を進めます。一回ごとの行動が、次の判断の根拠になります。",
      link: "一つの事業課題から始める",
      steps: [
        {
          title: "理解する。",
          description:
            "事業課題をデータ、顧客、市場と結びつけ、既知の事実と検証すべき仮説を整理します。",
          output: "明確な出発点",
        },
        {
          title: "優先する。",
          description:
            "期待できる価値、費用、不確実性を比較し、取り組む価値のある実験を選びます。",
          output: "検証できる成長仮説",
        },
        {
          title: "実行する。",
          description:
            "合意した計画に沿い、調査、分析、営業準備、フォローアップ、定型業務を AI で進めます。",
          output: "確認できる成果物",
        },
        {
          title: "学ぶ。",
          description:
            "実際の結果を出発点と比較し、根拠に基づいて拡大、変更、中止を判断します。",
          output: "根拠のある次の一手",
        },
      ],
    },
    work: {
      "label": "導入事例",
      "summary": "事業分析、国際事業、マーケティング、製造業での取り組み。",
      "impact": null,
      "cases": [
        {
          "name": "URide",
          "description": "事業データをつなぎ、分析に文脈を引き継ぐ。",
          "href": "/blog/uride-business-context"
        },
        {
          "name": "YOLO",
          "description": "国際業務をつなぎ、毎回の仕事から学ぶエージェント。",
          "href": "/blog/international-business-learning-loop"
        },
        {
          "name": "Ferns Kids",
          "description": "マーケティングとブランド認知を支えるエージェント連携。",
          "href": "/blog/ferns-kids-marketing-orchestration"
        },
        {
          "name": "製造業",
          "description": "具体的な業務課題に合わせた独自アルゴリズム。",
          "href": "/blog/manufacturing-custom-algorithms"
        }
      ],
      "title": "事業に合わせて、つくる。"
    },
    start: {
      label: "ご相談",
      title: "次のステージを、",
      accent: "強みとともに。",
      description:
        "新しい市場、営業のボトルネック、読み解けていない表計算データ。目の前にある機会から始めましょう。",
      cta: "成長について相談する",
      note: "業務範囲、料金、成果の指標を一緒に決めます。",
      steps: [
        {
          title: "成長させたいことを教えてください。",
          description:
            "事業、必要な判断、役立つ成果のイメージを共有してください。",
        },
        {
          title: "一つの取り組みに絞ります。",
          description:
            "データ、作業内容、最初の成果物を定め、進捗の測り方を合意します。",
        },
        {
          title: "実行、検証、振り返りを一緒に。",
          description:
            "分析と実行を進め、根拠を確認してから次の一手を決めます。",
        },
      ],
    },
    faq: {
      title: "よくあるご質問。",
      items: [
        {
          question: "「AI Quant」とは何ですか？",
          answer:
            "Quant は定量分析を意味し、データやモデルを用いて意思決定することです。YouWo はこの方法を事業成長に応用し、AI 分析と販売・流通・業務の実行を組み合わせます。",
        },
        {
          question: "ソフトウェアですか、実務支援ですか？",
          answer:
            "具体的な事業課題に対する実務支援から始めます。分析と実行の範囲を一緒に決め、AI で仕事を進めます。製品は「始める」からご覧いただけます。",
        },
        {
          question: "データを完全に整理しておく必要はありますか？",
          answer:
            "まずはお手元の資料で構いません。最初の相談で、有用な情報源、不足しているもの、有意義な分析や実験を始めるための根拠があるかを確認します。",
        },
        {
          question: "紹介されているプロジェクトは完了していますか？",
          answer:
            "現在進行中の取り組みです。現在のテーマと目標を紹介しています。納品と成果の測定が完了した段階で更新します。",
        },
      ],
    },
    footer: {
      label: "YouWo リンク",
      tagline: "次のステージを支える知性。",
      privacy: "プライバシー",
      terms: "利用規約",
      support: "サポート",
    },
  },
};
